import { type Theme, createTheme } from "@mui/material/styles";
import { darkTheme, lightTheme, themeFrom } from "./theme";
import { darkTokens, lightTokens } from "./tokens";

// The design system's theme, plus the components the console leans on that it leaves at MUI's
// defaults: tables, alerts, menus and multiline inputs.
// Layered on top rather than edited into `theme.ts`, so that file stays a plain copy of its source.

function withConsoleOverrides(base: Theme): Theme {
  const tokens = base.palette.tokens;
  const alert = (soft: string, ink: string, line: string) => ({
    backgroundColor: soft,
    color: ink,
    border: `1px solid color-mix(in srgb, ${line} 30%, transparent)`,
    "& .MuiAlert-icon": { color: line },
  });

  return createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${tokens.line.faint}`,
            padding: "10px 12px",
            fontSize: "0.8125rem",
            color: tokens.ink.secondary,
          },
          head: {
            fontFamily: tokens.font.mono,
            fontSize: "0.656rem",
            fontWeight: tokens.weight.medium,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            backgroundColor: tokens.surface.cardSunken,
            // muted, not faint: faint is 2.6:1 on the card, which this size cannot carry.
            color: tokens.ink.muted,
            borderBottom: `1px solid ${tokens.line.default}`,
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-child > .MuiTableCell-body": { borderBottom: "none" },
            "&.MuiTableRow-hover:hover": { backgroundColor: tokens.state.veil },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: tokens.radius.md, fontSize: "0.8125rem", alignItems: "center" },
          standardError: alert(
            tokens.semantic.dangerSoft,
            tokens.semantic.dangerInk,
            tokens.semantic.danger,
          ),
          standardWarning: alert(
            tokens.semantic.warningSoft,
            tokens.semantic.warningInk,
            tokens.semantic.warning,
          ),
          standardSuccess: alert(
            tokens.semantic.successSoft,
            tokens.semantic.successInk,
            tokens.semantic.success,
          ),
          standardInfo: alert(
            tokens.semantic.infoSoft,
            tokens.semantic.infoInk,
            tokens.semantic.info,
          ),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          // the input carries the padding, so a multiline root must not add its own
          multiline: { padding: 0 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { boxShadow: tokens.shadow.overlay, marginTop: 4 },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
            borderRadius: tokens.radius.sm,
            minHeight: 32,
            "&:hover": { backgroundColor: tokens.state.hover },
            "&.Mui-selected, &.Mui-selected:hover": {
              backgroundColor: tokens.surface.cardSunken,
              fontWeight: tokens.weight.medium,
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: { disableRipple: true },
        styleOverrides: { root: { color: tokens.line.strong, padding: 6 } },
      },
      MuiFormControlLabel: {
        styleOverrides: { label: { fontSize: "0.8125rem", color: tokens.ink.secondary } },
      },
    },
  });
}

/**
 * The console theme repainted in a tenant's accent, for the pages reached from a mailed link.
 *
 * Memoised by colour: a theme is not cheap to build, and the accent changes once per page load.
 */
const accented = new Map<string, Theme>();

export function accentedConsoleTheme(mode: "light" | "dark", accent: string): Theme {
  const key = `${mode}:${accent}`;
  const already = accented.get(key);
  if (already !== undefined) return already;
  const base = mode === "dark" ? darkTokens : lightTokens;
  const built = withConsoleOverrides(
    themeFrom(
      {
        ...base,
        accent: {
          ...base.accent,
          base: accent,
          hover: `color-mix(in srgb, ${accent} 85%, black)`,
        },
      },
      mode,
    ),
  );
  accented.set(key, built);
  return built;
}

export const consoleLightTheme: Theme = withConsoleOverrides(lightTheme);

export const consoleDarkTheme: Theme = withConsoleOverrides(darkTheme);
